
import { useState, useEffect, useRef, useCallback } from 'react';
import { Audience } from '@/store/videoFeatures';

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/**
 * A audiência se move em ONDAS, não em sorteios isolados por tick.
 *
 * A cada onda o contador ganha um alvo dentro da faixa e caminha até lá em
 * passos de 1 (às vezes 2), fazendo uma pausa curta ao chegar antes de mirar o
 * próximo. É isso que produz sequências longas — sobe de 151 até 170 ao longo
 * de vários ticks, respira, depois recua até 140 — em vez de ficar tremendo em
 * volta do valor configurado.
 *
 * O modelo anterior sorteava a variação a cada tick de forma independente. Como
 * todo passeio aleatório com reversão à média, ele se concentrava no centro: com
 * base 150 passava 51% do tempo entre 145 e 155 e só 1% acima de 170. Daí a
 * sensação de "fica sempre na mesma faixa".
 */
const WAVE_PAUSE_MIN = 2;
const WAVE_PAUSE_MAX = 6;

/**
 * Sorteia o alvo da próxima onda. A chance de mirar para cima cai conforme o
 * contador se aproxima do teto — é o que segura o número dentro da faixa sem
 * precisar que o clamp faça esse trabalho (quando o clamp trava o valor, o tick
 * passa sem mudança visível na tela).
 */
function pickWaveTarget(current: number, floor: number, ceiling: number): number {
  const span = ceiling - floor;
  const position = span > 0 ? (current - floor) / span : 0.5;
  const chanceOfRising = clamp(0.85 - 0.55 * position, 0.2, 0.85);
  const goingUp = Math.random() < chanceOfRising;

  const room = goingUp ? ceiling - current : current - floor;
  const distance = Math.max(1, Math.round(room * randomBetween(0.35, 1)));

  return clamp(goingUp ? current + distance : current - distance, floor, ceiling);
}

/**
 * Passo em direção ao alvo. A subida anda de 1 em 1 quase sempre e a descida
 * usa passo 2 com mais frequência: assim a subida ocupa mais ticks na tela do
 * que a queda, e o contador passa a maior parte do tempo crescendo.
 */
function stepToward(current: number, target: number): number {
  if (current === target) return 0;

  if (target > current) {
    return Math.min(Math.random() < 0.85 ? 1 : 2, target - current);
  }
  return -Math.min(Math.random() < 0.45 ? 1 : 2, current - target);
}

/** Micro-oscilação enquanto a onda "respira" no alvo. */
function pickJitter(): number {
  return Math.floor(Math.random() * 3) - 1;
}

export const useAudienceSimulation = (audience: Audience | undefined) => {
  const [displayViewers, setDisplayViewers] = useState<number>(0);
  const targetRef = useRef<number>(0);
  const currentRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const animStartRef = useRef<number>(0);
  const animFromRef = useRef<number>(0);
  const animToRef = useRef<number>(0);
  const pauseRef = useRef<number>(0);

  const isEnabled = audience?.enabled ?? false;
  const viewersCount = audience?.viewersCount;
  const message = audience?.message ?? '';
  const speed = audience?.speed ?? 'medium';
  const hasViewersCount = viewersCount !== undefined && viewersCount !== null && viewersCount !== 0;

  // Intervalo de atualização em ms por velocidade
  const speedIntervalRange: Record<string, [number, number]> = {
    slow:   [5000, 7000],
    medium: [3000, 5000],
    fast:   [1000, 3000],
  };

  // Easing function: easeOutCubic
  const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

  // Animate from current displayed value to target over 800ms
  const animateToValue = useCallback((from: number, to: number) => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (from === to) {
      setDisplayViewers(to);
      return;
    }

    animStartRef.current = performance.now();
    animFromRef.current = from;
    animToRef.current = to;
    const duration = 800;

    const step = (now: number) => {
      const elapsed = now - animStartRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      const current = Math.round(animFromRef.current + (animToRef.current - animFromRef.current) * eased);
      setDisplayViewers(current);
      currentRef.current = current;

      if (progress < 1) {
        animFrameRef.current = requestAnimationFrame(step);
      } else {
        animFrameRef.current = null;
      }
    };

    animFrameRef.current = requestAnimationFrame(step);
  }, []);

  // Efeito 1: apenas inicializa/reseta o contador quando viewersCount muda
  useEffect(() => {
    if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }

    if (!isEnabled || !hasViewersCount) {
      setDisplayViewers(0);
      currentRef.current = 0;
      targetRef.current = 0;
      return;
    }

    const base = viewersCount!;
    currentRef.current = base;
    targetRef.current = base;
    setDisplayViewers(base);
  }, [isEnabled, viewersCount, hasViewersCount]);

  // Efeito 2: gerencia o scheduling — re-executa quando speed muda sem resetar o contador
  useEffect(() => {
    if (!isEnabled || !hasViewersCount) return;

    if (intervalRef.current) { clearTimeout(intervalRef.current); intervalRef.current = null; }

    const base = viewersCount!;
    const floor = Math.max(1, Math.floor(base * 0.85));
    const ceiling = Math.ceil(base * 1.25);

    targetRef.current = pickWaveTarget(currentRef.current, floor, ceiling);
    pauseRef.current = 0;

    const scheduleNextChange = () => {
      const [minDelay, maxDelay] = speedIntervalRange[speed] ?? speedIntervalRange.medium;
      const delay = randomBetween(minDelay, maxDelay);
      intervalRef.current = setTimeout(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          scheduleNextChange();
          return;
        }

        const prev = currentRef.current;
        let next: number;

        if (pauseRef.current > 0) {
          pauseRef.current -= 1;
          next = clamp(prev + pickJitter(), floor, ceiling);
        } else {
          next = clamp(prev + stepToward(prev, targetRef.current), floor, ceiling);

          // Chegou no alvo: respira alguns ticks e mira o próximo.
          if (next === targetRef.current) {
            pauseRef.current = Math.round(randomBetween(WAVE_PAUSE_MIN, WAVE_PAUSE_MAX));
            targetRef.current = pickWaveTarget(next, floor, ceiling);
          }
        }

        animateToValue(prev, next);
        scheduleNextChange();
      }, delay);
    };

    scheduleNextChange();

    return () => {
      if (intervalRef.current) { clearTimeout(intervalRef.current); intervalRef.current = null; }
    };
  }, [isEnabled, viewersCount, hasViewersCount, speed, animateToValue]);

  const formattedMessage = hasViewersCount
    ? (message ? `${message}: ${displayViewers}` : `${displayViewers}`)
    : message;

  return {
    viewers: displayViewers,
    formattedMessage
  };
};
